import { useEffect, useState } from 'react';
import { Check, Copy, Link2 } from 'lucide-react';
import { api } from '../../../../services/api';
import { useWorkspace } from '../../WorkspaceContext';
import { finalizeAlias, sanitizeAliasInput } from '../../utils/alias';

const ManageBookingLink = () => {
  const { activeLocation, refresh } = useWorkspace();
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [togglingBooking, setTogglingBooking] = useState(false);
  const [aliasDraft, setAliasDraft] = useState('');
  const [aliasStatus, setAliasStatus] = useState({ state: 'idle', message: '' });
  const [savingAlias, setSavingAlias] = useState(false);

  const bookingPath = activeLocation?.slug ? `/book/${activeLocation.slug}` : null;
  const bookingUrl =
    typeof window !== 'undefined' && bookingPath
      ? `${window.location.origin}${bookingPath}`
      : bookingPath;
  const bookingOrigin =
    typeof window !== 'undefined' ? `${window.location.origin}/book/` : '/book/';

  useEffect(() => {
    if (activeLocation?.slug) {
      setAliasDraft(activeLocation.slug);
      setAliasStatus({ state: 'idle', message: '' });
    }
  }, [activeLocation?.id, activeLocation?.slug]);

  useEffect(() => {
    const alias = finalizeAlias(aliasDraft);
    if (!activeLocation?.slug) return;
    if (!alias || alias === activeLocation.slug) {
      setAliasStatus({ state: 'idle', message: '' });
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setAliasStatus({ state: 'checking', message: 'Checking availability…' });
      const result = await api.checkBookingAlias(alias, activeLocation.id);
      if (cancelled) return;

      if (result.error) {
        setAliasStatus({ state: 'error', message: result.error });
        return;
      }

      if (result.data?.available) {
        setAliasStatus({
          state: 'available',
          message: `Available: /book/${result.data.alias}`,
        });
      } else {
        setAliasStatus({
          state: 'taken',
          message: result.data?.error || 'That booking link is already taken',
        });
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [aliasDraft, activeLocation]);

  const copyLink = async () => {
    if (!bookingUrl) return;
    try {
      await navigator.clipboard.writeText(bookingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy link');
    }
  };

  const toggleBooking = async () => {
    if (!activeLocation) return;
    setTogglingBooking(true);
    setError('');
    const result = await api.updateWorkspaceLocation(activeLocation.id, {
      bookingEnabled: !(activeLocation.bookingEnabled !== false),
    });
    setTogglingBooking(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    await refresh();
  };

  const saveAlias = async (event) => {
    event.preventDefault();
    const alias = finalizeAlias(aliasDraft);
    if (!alias || !activeLocation || alias === activeLocation.slug) return;

    setSavingAlias(true);
    setError('');
    const result = await api.updateBookingAlias(alias, activeLocation.id);
    setSavingAlias(false);

    if (result.error) {
      setAliasStatus({ state: 'taken', message: result.error });
      setError(result.error);
      return;
    }

    setAliasDraft(result.data?.slug || alias);
    setAliasStatus({ state: 'saved', message: 'Booking link updated' });
    await refresh();
  };

  const aliasChanged =
    finalizeAlias(aliasDraft) && finalizeAlias(aliasDraft) !== activeLocation?.slug;
  const canSaveAlias = aliasChanged && aliasStatus.state === 'available' && !savingAlias;

  return (
    <div>
      {error && (
        <div className="mb-6 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <section className="bg-ink-900 p-4 text-sand-50 sm:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.18em] text-saffron-400 sm:text-xs sm:tracking-[0.22em]">
              Online booking
            </p>
            <h2 className="mt-2 font-display text-2xl font-light sm:text-3xl">
              {activeLocation?.name || 'Your location'}
            </h2>
            <p className="mt-2 max-w-lg text-sm text-sand-200/80">
              Clients pick a service, choose an open slot from this location&rsquo;s calendar, and
              land in its client book automatically. Each location has its own link.
            </p>
          </div>
          <button
            type="button"
            onClick={toggleBooking}
            disabled={togglingBooking || !activeLocation}
            className={`min-h-11 flex-none px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] transition-colors ${
              activeLocation?.bookingEnabled !== false
                ? 'bg-sand-50/10 text-sand-50 ring-1 ring-sand-100/30 hover:bg-sand-50/20'
                : 'bg-saffron-500 text-ink-900 hover:bg-saffron-400'
            }`}
          >
            {activeLocation?.bookingEnabled !== false ? 'Enabled' : 'Enable booking'}
          </button>
        </div>

        {bookingUrl && (
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <div className="flex min-w-0 flex-1 items-center gap-3 bg-ink-800 px-4 py-3">
              <Link2 className="h-4 w-4 flex-none text-saffron-400" />
              <p className="truncate text-sm text-sand-100">{bookingUrl}</p>
            </div>
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex min-h-11 items-center justify-center gap-2 bg-saffron-500 px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <a
              href={bookingPath}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center justify-center px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-sand-50 ring-1 ring-sand-100/30 transition-colors hover:bg-sand-50/10"
            >
              Open
            </a>
          </div>
        )}

        <form onSubmit={saveAlias} className="mt-8 border-t border-sand-100/15 pt-6">
          <label
            htmlFor="booking-alias"
            className="block text-xs font-semibold uppercase tracking-[0.14em] text-sand-200/70"
          >
            Custom booking alias
          </label>
          <p className="mt-2 text-sm text-sand-200/70">
            Choose a unique short name for this location&rsquo;s link. Only lowercase letters,
            numbers and hyphens.
          </p>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-stretch">
            <div className="flex min-w-0 flex-1 overflow-hidden bg-ink-800 ring-1 ring-sand-100/15 focus-within:ring-2 focus-within:ring-saffron-500">
              <span className="hidden flex-none items-center px-3 text-sm text-sand-300/60 sm:inline-flex">
                {bookingOrigin}
              </span>
              <span className="inline-flex flex-none items-center px-3 text-sm text-sand-300/60 sm:hidden">
                /book/
              </span>
              <input
                id="booking-alias"
                value={aliasDraft}
                onChange={(e) => setAliasDraft(sanitizeAliasInput(e.target.value))}
                spellCheck={false}
                autoCapitalize="none"
                autoCorrect="off"
                className="min-w-0 flex-1 bg-transparent px-3 py-3 text-base text-sand-50 placeholder:text-sand-300/40 focus:outline-none sm:text-sm"
                placeholder="ivory-and-coil"
              />
            </div>
            <button
              type="submit"
              disabled={!canSaveAlias}
              className="min-h-11 bg-saffron-500 px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {savingAlias ? 'Saving…' : 'Save alias'}
            </button>
          </div>

          {aliasStatus.message && (
            <p
              className={`mt-3 text-sm ${
                aliasStatus.state === 'available' || aliasStatus.state === 'saved'
                  ? 'text-saffron-400'
                  : aliasStatus.state === 'taken' || aliasStatus.state === 'error'
                    ? 'text-red-300'
                    : 'text-sand-200/70'
              }`}
            >
              {aliasStatus.message}
            </p>
          )}
        </form>
      </section>
    </div>
  );
};

export default ManageBookingLink;
